/** @module csi/view/Toolbar */
define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-class",
	"dojo/dom-style",
	"dojo/promise/all",
	"dojo/topic",
	"dijit/_Container",
	"dijit/_TemplatedMixin",
	"dijit/_WidgetBase",
	"dijit/_WidgetsInTemplateMixin",
	"dijit/MenuSeparator",
	"dijit/registry",
	"dijit/ToolbarSeparator",
	"underscore",
	"csi",
	"csi/html",
	"csi/locale",
	"csi/widget/Automation",
	"csi/widget/Menu",
	"./PluginFactory",
	"./ToolbarDropDown",
	"./ToolbarPopupMenuItem",
	"dojo/text!./Toolbar.html"
], function(array, declare, lang, domClass, domStyle, all, topic, _Container, _TemplatedMixin,
	_WidgetBase, _WidgetsInTemplateMixin, MenuSeparator, registry, ToolbarSeparator,
	_, csi, html, locale, Automation, Menu, PluginFactory, ToolbarDropDown, ToolbarPopupMenuItem, template) {

	const TOOLBAR2_PLUGINS = ["quickSearch"];

	var _toolbar = declare([_WidgetBase, _TemplatedMixin, _WidgetsInTemplateMixin, _Container], {
		// summary: Toolbar to display view actions
		templateString: template,

		// connection points
		dijitToolbarNode: null,
		dijitToolbar2Node: null, // Secondary toolbar is used for Toolbar2Plugins

		// Boolean: indicates that disabled actions should be hidden (for now just apply to drop-downs - not entire toolbar)
		bHideDisabledMenus: false,

		// ToolbarPlugin: modal plugin that disables all other plugins while valid
		pluginModal: null,

		// String[]: array of plugins that are immune from modal plugin
		pluginModeless: null,

		/** @property {array} pluginsMask - decide what plugins should be display, if null, then display all, if empty, hide all */
		pluginsMask: null,

		/** @property {boolean} tb1visible - true if the primary toolbar is visible */
		tb1Visible: false,

		// Boolean: true if secondary toolbar is visible
		tb2Visible: false,

		// csi.view.Widget: view containing toolbar
		view: null,

		// Object: mapping from plugin name to widget created
		_pluginWidgets: null,

		constructor: function() {
			this._pluginWidgets = {};
		},

		/**
		 * Remove trailing, leading and duplicated separators, disable plugins with sub-menu if all it's child's are disabled
		 * @param {domObject[]} children
		 * @param {boolean} bHideDisabledMenus
		 * @param {boolean} bIgnoreRight - ignore right aligned toolbar members. True for secondary toolbar and submenus
		 * @return {boolean} - return true if valid. Toolbar/menu is valid if has at least one visible widget that is not a separator
		 */
		cleanupToolbarChildren: function(children, bHideDisabledMenus, bIgnoreRight) {
			// summary: hide separators at beginning or end of toolbar/menu, or that are contiguous; also, hide disabled menu items if so configured
			var bFirst = true;
			var bValid = false;
			var previousSeparator;
			var firstPluginNum = !bIgnoreRight && this.getFirstPluginNum(); // very left plugin index (considering right plugins)

			_.each(children, function(w, idx) {
				if (firstPluginNum && (idx == firstPluginNum)) { // first plugin after couple of floating right ones
					previousSeparator = null;
					bFirst = true;
				}
				if (!w.pluginName) {
					// hide this separator if it is leading or if previous visible plugin was also a separator, but continue using previous separator for analysis
					html.setVisible(w, !previousSeparator && !bFirst);
					previousSeparator = previousSeparator || w;
				} else if (!html.isVisible(w.domNode, true)) {
					// skip hidden items
					return;
				} else {
					// if widget is menu, then cleanup its children
					var menuObj = w.dropDown || w.popup;

					if (menuObj instanceof Menu) {
						var menuChildren = menuObj.getChildren();

						// hide disabled menu items if so configured
						if (bHideDisabledMenus && !w.showDisabledItems) {
							_.each(menuChildren, function(w) {
								if (w.pluginName) {
										html.setVisible(w.domNode, !w.get("disabled"));
								}
							});
						}
						// cleanup menu's children
						var bValidChildren = this.cleanupToolbarChildren(menuChildren, bHideDisabledMenus, true);

						// if plugin is enabled, check if it has children and they all are disabled and then disable the plugin
						if (bValidChildren && menuChildren) {
							bValidChildren = menuChildren.some((child) => child && child.label && !child.get("disabled"));
						}

						// disable actions drop-down button if no actions available
						w.set("disabled", !bValidChildren);
					}

					// toolbar/menu is valid if has at least one visible widget that is not a separator
					bValid = true;
					previousSeparator = null;
				}

				bFirst = false;
			}, this);

			// Hide trailing separator
			if (previousSeparator) {
				html.setVisible(previousSeparator, false);
			}

			return bValid;
		},

		postCreate: function() {
			// summary: prevent the handling of key press by the toolbar
			this.dijitToolbarNode._onContainerKeypress = function() { };

			// This will prevent dijit toolbar from capturing HOME, END, LEFT and RIGHT ARROW and using it
			// for navigation along the toolbar as it is defined in dijit/_KeyNavMixin.js->postCreate()
			// instead, we want these keys to be used for editing data in controls
			this.dijitToolbarNode._keyNavCodes = {};

			if (this.dijitToolbar2Node) {
				this.dijitToolbar2Node._onContainerKeypress = function() { };
				this.dijitToolbar2Node._keyNavCodes = {};
			}

			this.inherited(arguments);
		},

		setView: function(/*csi.view.Widget*/view) {
			// summary: set view controller and populate plugins returning promise
			this.view = view;
			return this._processPlugins(view.getContext(), view.plugins);
		},

		setModalPlugin: function(/*ToolbarPlugin?*/plugin, /*String[]?*/modeless) {
			// summary: set modal plugin which will control disabled status of other widgets
			this.pluginModal = plugin;
			this.pluginModeless = modeless;
		},

		enablePlugins: function() {
			// summary: enable plugins if user has access to their actions based on view and possibly view selections
			this._enablePlugins(true);
		},

		/**
		 * Enable plugins if given context; otherwise disable
		 * @param {boolean} bEnable
		 */
		_enablePlugins: function(bEnable) {
			var view = this.view;
			if (!view) {
				return;
			}

			var obj = this._pluginWidgets;
			var modal = this.pluginModal;
			var modeless = this.pluginModeless;
			var ctx = view.getContext();
			var p;
			var bAllow;
			var bShowPlugin;
			var t1Visible = false; // First Toolbar visible
			var t2Visible = false; // Second Toolbar visible
			const tb1DomNode = this.dijitToolbarNode.domNode;

			// set context for all plugins
			ctx.applyActionContext(this.domNode, "");

			for (var i in obj) {
				p = obj[i];

				// if modal plugin, then disable all other plugins;
				// modeless plugins are those on toolbar, not within menus; so, prioritize parentPluginName over pluginName
				bAllow = !modal || (p == modal) || (modeless && array.indexOf(modeless, p.parentPluginName || p.pluginName) >= 0);
				p.enablePlugin((bAllow && bEnable) ? ctx : null);
				bShowPlugin = p.canShowPlugin();

				const bDisplayAll = !this.pluginsMask;

				if (bDisplayAll) {
					html.setVisible(p, bShowPlugin);
				} else if (!this.pluginsMask.length) {
					html.setVisible(p, false);
				} else {
					html.setVisible(p, bShowPlugin && _.contains(this.pluginsMask, p.pluginName));
				}

				if (bShowPlugin) {
					if (p.bT2Plugin) {
						t2Visible = true;
					} else {
						t1Visible = true;
					}
				}
			}

			// if toolbar should hide disabled items, then do so now
			if (t1Visible) {
				this.cleanupToolbarChildren(this.dijitToolbarNode.getChildren(), this.bHideDisabledMenus);
				this.resizeToolbar();
			}

			if (t2Visible) {
				this.cleanupToolbarChildren(this.dijitToolbar2Node.getChildren(), this.bHideDisabledMenus, true);
			}

			html.setVisible(tb1DomNode, t1Visible);
			html.setVisible(this.dijitToolbar2ContainerNode, t2Visible);
			html.setVisibility(this.dijitToolbar2Node, t2Visible);

			// resize view when hiding/showing t2 toolbar
			if (this.tb2Visible !== t2Visible) {
				view._resizeView();
			}

			this.tb2Visible = t2Visible;
			this.tb1Visible = t1Visible;

			view.onEnablePlugins();
		},

		destroy: function() {
			// summary: clean up references just in case circular loops
			csi.applyForEach(this._pluginWidgets, function(value, key) {
				delete value.actionToolbar;
			});

			delete this._pluginWidgets;
			delete this.pluginModal;
			delete this.view;

			this.inherited(arguments);
		},

		disablePlugins: function() {
			// summary: disable all toolbar widgets except for possible modal plugin
			this._enablePlugins(false);
		},

		getFirstPluginName: function() {
			// summary: return first toolbar plugin that is not in pluginsRight
			var name;
			var pluginsRight = this.view && this.view.pluginsRight;

			_.some(this.dijitToolbarNode.getChildren(), function(w) {
				if (pluginsRight && _.contains(pluginsRight, w.pluginName)) {
					return false;
				}

				name = w.pluginName;
				return !!name;
			});

			return name;
		},

		getFirstPluginNum: function() {
			// summary: return index of first toolbar plugin that is not in pluginsRight
			var num = 0;
			var pluginsRight = this.view && this.view.pluginsRight;
			_.some(this.dijitToolbarNode.getChildren(), function(w, idx) {
				var name = w.pluginName;
				num = idx;
				return pluginsRight ? !_.contains(pluginsRight, name) : true;
			});

			return num;
		},

		getPlugin: function(/*String*/plugin) {
			// summary: get widget representing plugin
			return this._pluginWidgets[plugin];
		},

		getPluginParams: function(/*String*/plugin, /*Object?*/params) {
			// summary: return parameters used for creating plugin
			var obj = {
				actionToolbar: this,
				pluginName: plugin
			};

			Automation.params(this.view.defView.getHtmlKey(), "plugin", plugin, obj);

			return params ? lang.mixin(obj, params) : obj;
		},

		addPlugin: function(/*Context*/ctx,
							/*String|Object*/plugin,
							/*String?*/beforePlugin,
							/*Object*/unused,
							/*Widget?*/parentMenu) {
			// summary: creates widget for plugin and adds to toolbar before another plugin if specified
			var menu, w;
			var ACTION_SEPARATOR = "|";

			if (!plugin) {
				return null;
			} else if ((plugin === ACTION_SEPARATOR) || (plugin.m_id === ACTION_SEPARATOR)) {
				// no need to add separator when printing
				if (ctx.bPrint) {
					return null;
				}

				// create appropriate separator
				w = parentMenu ? new MenuSeparator() : new ToolbarSeparator();
			} else if (plugin.hasAccess) {
				// plugin is action
				w = PluginFactory.create(this.getPluginParams(plugin.m_id, {defAction: plugin}), ctx, !!parentMenu);
			} else if (_.isObject(plugin)) {
				// no need to add menu when printing
				if (ctx.bPrint) {
					return null;
				}

				// plugin is object containing sub-plugins, so create drop-down button with menu
				menu = new Menu({
					"class": plugin.className || ""
				});

				// the subscription topic is sent when the drop-down menu on the toolbar is about to be instantiated.
				// The following object is passed as a parameter of the message:
				// { ctx: Context of the root node of the view
				//   children: the array of plugin names/actions for the given drop-down
				//   menu: Menu of the drop-down
				//   name: the name of the parent menu item for this drop-down
				// }

				// FIX: use custom toolbar plugin for Export Menu rather than doing this???
				topic.publish("/csi/toolbar/menu", {
					ctx: ctx,
					children: plugin.children,
					menu: menu,
					name: plugin.name,
					icon: plugin.icon
				});

				// populate menu with plugins
				// FIX: append menu plugins to deferred list?
				if (this._processPlugins(ctx, plugin.children, unused, menu)) {
					var params = this.getPluginParams(plugin.name, {
						id: plugin.id,
						title: plugin.title || locale.tooltip(plugin.name) || locale.resource(plugin.name),
						label: _.escape(plugin.label || locale.resource(plugin.name)),
						iconClass: plugin.icon,
						showLabel: plugin.showLabel || (!plugin.materialIcon && !plugin.icon),
						materialIcon: plugin.materialIcon || ''
					});

					// all menu items need to know the name of their parent plugin
					_.each(menu.getChildren(), item => {
						item.parentPluginName = plugin.name;
					});

					if (parentMenu) {
						const ctr = plugin.classToolbarPlugin || ToolbarPopupMenuItem;
						w = new ctr(lang.mixin(params, {
							popup: menu,
							showDisabledItems: plugin.showDisabledItems,
						}));
					} else {
						const ctr = plugin.classToolbarPlugin || ToolbarDropDown;
						w = new ctr(lang.mixin(params, {
							dropDown: menu,
							pluginChildren: plugin.children,
							iconClass: plugin.icon,
							showLabel: params.showLabel,
							showDisabledItems: plugin.showDisabledItems,
						}));
					}
				}
			} else {
				// create plugin based on registered type
				w = PluginFactory.create(this.getPluginParams(plugin, unused), ctx, !!parentMenu);
			}

			// add widget to toolbar and store by plugin name
			if (this.addPluginWidget(w, beforePlugin, parentMenu)) {
				// since menu is at root level of DOM, need to set action context explicitly for each menu
				if (menu) {
					// TODO: don't think this is necessary any longer
					ctx.applyActionContext(menu.domNode, "");
				}
				return w;
			}

			return null;
		},

		addPluginWidget: function(/*Widget*/w, /*String?*/beforePlugin, /*Widget?*/menu) {
			// summary: adds widget to toolbar before another plugin if specified
			if (w) {
				var bw = beforePlugin ? this.getPlugin(beforePlugin) : null;
				var pw = (bw ? registry.getEnclosingWidget(bw.domNode.parentNode) : menu) || this.dijitToolbarNode;
				var index = bw ? html.getElementIndex(bw.domNode) : undefined;

				pw.addChild(w, index);

				// FIX: not sure why this is needed for LastVisited to work - addChild does not seem to add "w" to
				// children, whch means startup will not be called on "w" when "pw" is started
				if (!w._started) {
					w.startup();
				}

				if (w.domNode) {
					domClass.add(w.domNode, "csi-toolbar-btn-" + w.pluginName);
					if (w.iconNode) {
						domClass.add(w.iconNode, "csi-toolbar-btn-icon-" + w.pluginName);
					}
				}

				if (w.pluginName) {
					this._pluginWidgets[w.pluginName] = w;
					return w;
				}
			}

			return null;
		},

		removePlugins: function(/*String[]*/plugins, /*Boolean*/bDestroy) {
			// summary: remove given plugin widgets from toolbar
			var obj = this._pluginWidgets;
			var a = [];

			if (obj) {
				array.forEach(plugins, function(p) {
					var w = obj[p];
					if (w) {
						// destroy menu items
						if (w.pluginChildren) {
							this.removePlugins(w.pluginChildren, bDestroy);
						}

						if (bDestroy) {
							w.destroy();
						} else {
							if (w.bT2Plugin) {
								this.dijitToolbar2Node.removeChild(w);
							} else {
								this.dijitToolbarNode.removeChild(w);
							}
							a.push(w);
						}

						delete obj[p];
					}
				}, this);
			}

			return a;
		},

		removeAllPlugins: function() {
			// summary: remove all children
			var tb = this.dijitToolbarNode;
			var children = tb.getChildren();
			array.forEach(children, tb.removeChild, tb);

			if (this.dijitToolbar2Node) {
				tb = this.dijitToolbar2Node;
				var children2 = tb.getChildren();
				array.forEach(children2, tb.removeChild, tb);
				children = children.concat(children2);
			}

			return children;
		},

		/**
		 * Set minimum toolbar width to prevent right plugins overlap left plugins
		 */
		resizeToolbar() {
			const {view, _pluginWidgets} = this;
			const pluginsRight = view && view.pluginsRight;

			if (_.isEmpty(pluginsRight)) {
				return;
			}

			let lastVisiblePlugin = null;
			let offset = 0;

			for (let pluginName in _pluginWidgets) {
				const p = _pluginWidgets[pluginName];

				if (!p.bT2Plugin && p.canShowPlugin()) {
					if (pluginsRight.includes(pluginName)) {
						offset += p.domNode.offsetWidth + 4; // 4px is for space between plugins
					} else {
						lastVisiblePlugin = p;
					}
				}
			}

			if (lastVisiblePlugin && offset) {
				// NOTE: This does not seem to work correctly if lastVisiblePlugin uses float:right styling
				const tb1DomNode = this.dijitToolbarNode.domNode;
				const toolbarWidth = lastVisiblePlugin.domNode.getBoundingClientRect().right - tb1DomNode.getBoundingClientRect().left + offset;
				html.setPixels(tb1DomNode, html.MINWIDTH, toolbarWidth);
			}
		},

		validateAndRemovePlugins: function(/*Context*/ctx) {
			// summary: remove plugins not passing validation
			var obj = this._pluginWidgets;
			if (!obj) return false;

			var pluginsToRemove = [];

			for (var pluginName in obj) {
				var w = obj[pluginName];
				var action = w.defAction;
				if (action) {
					var bPop = ctx.push(action.m_path);
					if (!action.validate(ctx))
						pluginsToRemove.push(pluginName);
					if (bPop) ctx.pop();
				}
			}

			this.removePlugins(pluginsToRemove, true);

			var len = this.dijitToolbarNode.getChildren().length;
			if (this.dijitToolbar2Node) {
				len += this.dijitToolbar2Node.getChildren().length;
			}
			// return true if toolbar still contains any plugins
			return !!len;
		},

		_processPlugins: function(/*Context*/ctx, /*String|Object|Action*/plugins, /*Object*/unused, /*Widget?*/menu) {
			// summary: add plugins to given widget
			// 		Use registry to lookup appropriate plugin widget for string plugins;
			// 		Object plugins are used to create menus (by default using DropDownButton) and take the form:
			//			{name: String, type: dojoType, children: Array}
			const toolbar2Available = !menu && this.dijitToolbar2Node;
			const dfds = [];
			let hasPlugin;
			let hasSecondaryPlugin;
			let w;

			array.forEach(plugins, function(p) {
				let node = menu;
				let bT2Plugin = false;

				if (toolbar2Available && p && (p.bSecondaryToolbar || TOOLBAR2_PLUGINS.includes(p))) {
					// plugin belongs to toolbar 2
					node = this.dijitToolbar2Node;
					bT2Plugin = hasSecondaryPlugin = true;
				}

				if (w = this.addPlugin(ctx, p, null, unused, node)) {
					w.bT2Plugin = bT2Plugin;
					// FIX?: menus not currently supported
					if (w._deferred) {
						dfds.push(w._deferred);
					}
					hasPlugin = true;
				}
			}, this);

			// show secondary toolbar so that space is allocated on page for it; if not now, then widgets that detect available height
			// could overestimate available space (or we would have to call resize again). This also reduces jitter in most cases.
			// There are only a few cases where the secondary toolbar may first appear and then disappear. There are more cases where
			// it does not.
			if (hasSecondaryPlugin) {
				html.setVisible(this.dijitToolbar2ContainerNode, true);
			}

			return hasPlugin ? all(dfds) : null;
		}
	});

	/**
	 * move specified toolbar plugin to secondary toolbar
	 * @param {string} id - plugin id
	 * @param {boolean} bAdd - add ("true")/remove("false") plugin to/from second toolbar
	 */
	_toolbar.setPluginForToolbar2 = function(id, bAdd) {
		if (!id) {
			return;
		}
		if (bAdd) {
			Toolbar2Plugins.push(id);
		} else {
			Toolbar2Plugins = _.without(Toolbar2Plugins, id);
		}
	};

	return _toolbar;

});